import type { AttachmentItem } from "./types";

export const GROUP_ORDER = ["Financial", "Identity", "General", "Custom"];

export const TAB_DESCRIPTIONS: Record<string, string> = {
  applicant: "Require documents from applicants",
  occupant: "Require documents from occupants",
  guarantor: "Require documents from guarantors",
};

const VERO_MINIMUMS: Record<string, number> = {
  paystubs: 3,
  "bank statements": 2,
};

// Mirrors backend LEGACY_FINANCIAL_DOC_DEFAULTS['paystub'] = 3.
export const PAYSTUB_VERO_DEFAULT = 3;

export const toDocKey = (value: string): string => value.trim().toLowerCase();

export const getItemDocKey = (item: {
  genericName?: string;
  label: string;
}): string => toDocKey(item.genericName || item.label);

export const getComparableQty = (
  item: Pick<AttachmentItem, "requiredCount" | "requiredQty">
): number | null => {
  if (item.requiredCount != null) return item.requiredCount;
  if (item.requiredQty === null) return null;
  return item.requiredQty ?? 1;
};

export const getComparableRunwayMonths = (
  item: Pick<AttachmentItem, "runwayMonths">
): number | null => {
  if (item.runwayMonths == null) return null;
  return item.runwayMonths;
};

export const getVeroMinimum = (docLabel: string): number => {
  const lower = docLabel.toLowerCase();
  const match = Object.entries(VERO_MINIMUMS).find(([key]) =>
    lower.includes(key)
  );
  return match ? match[1] : 1;
};

export const isPaystubDoc = (docLabel: string): boolean => {
  return docLabel.toLowerCase().includes("paystub");
};

// getDomesticDocMapping pluralizes "1099" → "1099s" unconditionally, but at
// qty=1 the applicant-facing resolver returns the singular raw name. Keep the
// manager label in lockstep so "1099s" doesn't appear next to qty=1.
const SINGULAR_LABELS: Record<string, string> = {
  "1099s": "1099",
};

export const singularizeAtQtyOne = (docLabel: string): string =>
  SINGULAR_LABELS[docLabel.toLowerCase()] ?? docLabel;

// Composes the runway row label from runwayMonths. Optimistic — reflects
// stepper edits before save. Returns null when runway isn't active.
export const buildRunwayLabel = (
  runwayMonths: number | null | undefined
): string | null => {
  if (!runwayMonths || runwayMonths < 1) return null;
  return runwayMonths === 1
    ? "1 Month of Paystubs"
    : `${runwayMonths} Months of Paystubs`;
};

/**
 * Determines which applicant types should receive a new custom doc based on
 * the active tab and which personas are overridden.
 *
 * - Applicant tab: creates for applicant + all inherited (non-overridden) personas
 * - Overridden tab: creates only for that persona
 * - Inherited tabs never show the input, so they don't call this
 */
export const getTargetApplicantTypes = (
  personas: { key: string; applicantType: number }[],
  activePersonaKey: string | undefined,
  overriddenKeys: Set<string>,
  primaryPersonaKey?: string
): number[] => {
  const resolvedPrimaryPersonaKey = primaryPersonaKey || personas[0]?.key;
  if (activePersonaKey === resolvedPrimaryPersonaKey) {
    // Applicant tab: applicant + all inherited personas.
    return personas
      .filter(
        (p) => p.key === resolvedPrimaryPersonaKey || !overriddenKeys.has(p.key)
      )
      .map((p) => p.applicantType);
  }
  const active = personas.find((persona) => persona.key === activePersonaKey);
  return active ? [active.applicantType] : [];
};

// applicantType -> docKey -> item. Dedupes by docKey — duplicates bounce
// the sync effect across renders.
export const indexItemsByTypeAndKey = (
  items: AttachmentItem[]
): Map<number, Map<string, AttachmentItem>> => {
  const index = new Map<number, Map<string, AttachmentItem>>();
  items.forEach((item) => {
    let personaMap = index.get(item.applicantType);
    if (!personaMap) {
      personaMap = new Map();
      index.set(item.applicantType, personaMap);
    }
    const docKey = getItemDocKey(item);
    if (!personaMap.has(docKey)) {
      personaMap.set(docKey, item);
    }
  });
  return index;
};

const EMPTY_MAP: Map<string, AttachmentItem> = new Map();

// Returns {id, checked, qty} updates for inherited personas that drifted
// from the applicant's current state.
export const getInheritedSyncUpdates = (
  personas: { key: string; applicantType: number; toggleField?: string }[],
  items: AttachmentItem[],
  overriddenKeys: Set<string>,
  values?: Record<string, any>,
  primaryPersonaKey?: string
): {
  id: string | number;
  checked: boolean;
  qty: number | null;
  runwayMonths: number | null;
}[] => {
  const resolvedPrimaryPersonaKey = primaryPersonaKey || personas[0]?.key;
  const applicant = personas.find((p) => p.key === resolvedPrimaryPersonaKey);
  if (!applicant) return [];

  const index = indexItemsByTypeAndKey(items);
  const applicantItemsByDocKey = index.get(applicant.applicantType) || EMPTY_MAP;
  const updates: {
    id: string | number;
    checked: boolean;
    qty: number | null;
    runwayMonths: number | null;
  }[] = [];

  personas
    .filter((p) => p.key !== applicant.key)
    .forEach((p) => {
    if (overriddenKeys.has(p.key)) return;
    if (values && p.toggleField && !values[p.toggleField]) return;

    const personaItemsByDocKey = index.get(p.applicantType) || EMPTY_MAP;
    applicantItemsByDocKey.forEach((appItem, docKey) => {
      const match = personaItemsByDocKey.get(docKey);
      if (!match) return;

      const appChecked = !!appItem.checked;
      const appQty = getComparableQty(appItem);
      const appRunway = getComparableRunwayMonths(appItem);
      const matchChecked = !!match.checked;
      const matchQty = getComparableQty(match);
      const matchRunway = getComparableRunwayMonths(match);

      if (
        appChecked !== matchChecked ||
        appQty !== matchQty ||
        appRunway !== matchRunway
      ) {
        updates.push({
          id: match.id,
          checked: appChecked,
          qty: appQty,
          runwayMonths: appRunway,
        });
      }
    });
    });

  return updates;
};

/**
 * Returns keys of personas whose AR data diverges from the applicant's —
 * the only signal that survives a page refresh.
 */
export const getPersonasWithDataDivergence = (
  personas: { key: string; applicantType: number; toggleField?: string }[],
  items: AttachmentItem[],
  values?: Record<string, any>,
  primaryPersonaKey?: string
): Set<string> => {
  const divergent = new Set<string>();
  const resolvedPrimaryPersonaKey = primaryPersonaKey || personas[0]?.key;
  const applicant = personas.find((p) => p.key === resolvedPrimaryPersonaKey);
  if (!applicant) return divergent;

  const index = indexItemsByTypeAndKey(items);
  const applicantItemsByDocKey = index.get(applicant.applicantType) || EMPTY_MAP;

  personas
    .filter((p) => p.key !== applicant.key)
    .forEach((p) => {
    if (values && p.toggleField && !values[p.toggleField]) return;

    const personaItemsByDocKey = index.get(p.applicantType);
    if (!personaItemsByDocKey) return;

    // Short-circuits on first orphan custom or shared-doc drift.
    const personaEntries = Array.from(personaItemsByDocKey);
    const isDivergent = personaEntries.some(([docKey, item]) => {
      const appItem = applicantItemsByDocKey.get(docKey);
      if (!appItem) return !item.isDefault;
      const appChecked = !!appItem.checked;
      const appQty = getComparableQty(appItem);
      const appRunway = getComparableRunwayMonths(appItem);
      const itemChecked = !!item.checked;
      const itemQty = getComparableQty(item);
      const itemRunway = getComparableRunwayMonths(item);
      return (
        appChecked !== itemChecked ||
        appQty !== itemQty ||
        appRunway !== itemRunway
      );
    });

    if (isDivergent) divergent.add(p.key);
    });

  return divergent;
};

// Custom docs that exist only for this persona (orphaned when the persona
// resets to inherited).
export const getOrphanedDocIds = (
  applicantType: number,
  primaryApplicantType: number,
  items: AttachmentItem[]
): (string | number)[] => {
  const primaryDocs = new Set(
    items
      .filter((i) => i.applicantType === primaryApplicantType)
      .map(getItemDocKey)
  );

  return items
    .filter(
      (i) =>
        i.applicantType === applicantType &&
        !i.isDefault &&
        !primaryDocs.has(getItemDocKey(i))
    )
    .map((i) => i.id);
};
